import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Users, MessageSquare, FileVideo, BarChart3, Settings, Shield, LogOut, Menu, X, ChevronLeft, FileSignature, DollarSign, Trash2, Building2, HelpCircle, ScrollText } from 'lucide-react';
import { useAuth } from '../../context/AuthContext';
import { useTheme } from '../../context/ThemeContext';
import { Button } from '../ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { ThemeToggle } from '../ui/ThemeToggle';
import UserManagement from './UserManagement';
import MessagingPanel from './MessagingPanel';
import ContentManagement from './ContentManagement';
import AnalyticsDashboard from './AnalyticsDashboard';
import ContractTierManagement from './ContractTierManagement';
import AdminSettings from './AdminSettings';
import BulkVideoManagement from './BulkVideoManagement';
import BankWithdrawalManagement from './BankWithdrawalManagement';
import FAQManagement from './FAQManagement';
import EnhancedAuditLogs from './EnhancedAuditLogs';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const AdminDashboard = () => {
  const { user, logout, token } = useAuth();
  const { isDark } = useTheme();
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('analytics');
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [sidebarOpen, setSidebarOpen] = useState(false);

  useEffect(() => {
    // Check if user is admin
    if (!user || user.role !== 'admin') {
      navigate('/browse');
      return;
    }
    
    fetchStats();
  }, [user, navigate]);

  const fetchStats = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/admin/stats`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setStats(response.data);
    } catch (error) {
      console.error('Error fetching stats:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = () => {
    logout();
    navigate('/');
  };

  const handleTabChange = (tabId) => {
    setActiveTab(tabId);
    setSidebarOpen(false); // Close sidebar on mobile after selection
  };

  const menuItems = [
    { id: 'analytics', label: 'Analytics', icon: <BarChart3 className="w-5 h-5" /> },
    { id: 'users', label: 'Users', icon: <Users className="w-5 h-5" /> },
    { id: 'contract', label: 'The Contract', icon: <FileSignature className="w-5 h-5" /> },
    { id: 'content', label: 'Content', icon: <FileVideo className="w-5 h-5" /> },
    { id: 'bulk-videos', label: 'Bulk Videos', icon: <Trash2 className="w-5 h-5" /> },
    { id: 'bank', label: 'Bank & Withdrawals', icon: <Building2 className="w-5 h-5" /> },
    { id: 'audit', label: 'Audit Logs', icon: <ScrollText className="w-5 h-5" /> },
    { id: 'faq', label: 'FAQ Management', icon: <HelpCircle className="w-5 h-5" /> },
    { id: 'messages', label: 'Messages', icon: <MessageSquare className="w-5 h-5" /> },
    { id: 'settings', label: 'Settings', icon: <Settings className="w-5 h-5" /> },
  ];

  return (
    <div className={`min-h-screen ${isDark ? 'bg-gradient-to-b from-black via-gray-900 to-black' : 'bg-gradient-to-b from-gray-50 via-white to-gray-100'}`}>
      {/* Header */}
      <div className={`${isDark ? 'bg-black/80 border-gray-800' : 'bg-white/90 border-gray-200'} backdrop-blur-xl border-b sticky top-0 z-50`}>
        <div className="px-4 md:px-6 py-3 md:py-4 flex justify-between items-center">
          <div className="flex items-center gap-3">
            {/* Mobile menu button */}
            <button
              onClick={() => setSidebarOpen(!sidebarOpen)}
              className={`lg:hidden p-2 rounded-lg ${isDark ? 'hover:bg-gray-800 text-white' : 'hover:bg-gray-100 text-gray-800'}`}
            >
              {sidebarOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
            </button>
            
            <button
              onClick={() => navigate('/browse')}
              className={`p-2 rounded-lg hidden sm:block ${isDark ? 'hover:bg-gray-800 text-gray-400 hover:text-white' : 'hover:bg-gray-100 text-gray-500 hover:text-gray-800'}`}
            >
              <ChevronLeft className="w-5 h-5" />
            </button>
            
            <Shield className="w-6 h-6 md:w-8 md:h-8 text-yellow-400" />
            <div>
              <h1 className="text-lg md:text-2xl font-black bg-gradient-to-r from-yellow-400 to-purple-500 bg-clip-text text-transparent">
                Admin Portal
              </h1>
              <p className={`text-xs hidden sm:block ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>I Got Soul - System Administrator</p>
            </div>
          </div>
          
          <div className="flex items-center gap-2 md:gap-4">
            {/* Theme Toggle */}
            <ThemeToggle 
              variant="ghost" 
              className={isDark ? 'text-gray-400 hover:text-white hover:bg-gray-800' : 'text-gray-600 hover:text-gray-900 hover:bg-gray-100'}
            />
            
            <div className="text-right hidden sm:block">
              <p className={`text-sm font-semibold ${isDark ? 'text-white' : 'text-gray-900'}`}>{user?.username}</p>
              <p className={`text-xs ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>{user?.email}</p>
            </div>
            <Button
              onClick={handleLogout}
              variant="outline"
              size="sm"
              className="border-red-500 text-red-400 hover:bg-red-500 hover:text-white"
            >
              <LogOut className="w-4 h-4 md:mr-2" />
              <span className="hidden md:inline">Logout</span>
            </Button>
          </div>
        </div>
      </div>

      <div className="flex relative">
        {/* Mobile Sidebar Overlay */}
        {sidebarOpen && (
          <div 
            className="fixed inset-0 bg-black/50 z-40 lg:hidden"
            onClick={() => setSidebarOpen(false)}
          />
        )}

        {/* Sidebar */}
        <div className={`
          fixed lg:static inset-y-0 left-0 z-50
          w-64 ${isDark ? 'bg-gray-900 lg:bg-gray-900/50' : 'bg-white lg:bg-gray-50/80'} 
          min-h-screen border-r ${isDark ? 'border-gray-800' : 'border-gray-200'} p-4
          transform transition-transform duration-300 ease-in-out
          ${sidebarOpen ? 'translate-x-0' : '-translate-x-full lg:translate-x-0'}
          lg:block
        `}>
          {/* Mobile sidebar header */}
          <div className={`lg:hidden flex items-center justify-between mb-4 pb-4 border-b ${isDark ? 'border-gray-800' : 'border-gray-200'}`}>
            <div className="flex items-center gap-2">
              <Shield className="w-6 h-6 text-yellow-400" />
              <span className={`font-bold ${isDark ? 'text-white' : 'text-gray-900'}`}>Menu</span>
            </div>
            <button
              onClick={() => setSidebarOpen(false)}
              className={`p-2 rounded-lg ${isDark ? 'hover:bg-gray-800 text-gray-400' : 'hover:bg-gray-100 text-gray-500'}`}
            >
              <X className="w-5 h-5" />
            </button>
          </div>

          <div className="space-y-2">
            {menuItems.map((item) => (
              <button
                key={item.id}
                onClick={() => handleTabChange(item.id)}
                className={`w-full flex items-center gap-3 px-4 py-3 rounded-lg transition-all ${
                  activeTab === item.id
                    ? 'bg-gradient-to-r from-yellow-400 to-purple-500 text-white shadow-lg'
                    : isDark 
                      ? 'text-gray-400 hover:bg-gray-800 hover:text-white'
                      : 'text-gray-600 hover:bg-gray-100 hover:text-gray-900'
                }`}
              >
                {item.icon}
                <span className="font-medium">{item.label}</span>
              </button>
            ))}
          </div>

          {/* Quick Stats - Hidden on mobile */}
          {stats && (
            <div className="mt-8 space-y-3 hidden lg:block">
              <h3 className={`text-xs font-semibold uppercase ${isDark ? 'text-gray-500' : 'text-gray-400'}`}>Quick Stats</h3>
              <Card className={`${isDark ? 'bg-gray-800/50 border-gray-700' : 'bg-white border-gray-200'}`}>
                <CardContent className="p-3">
                  <div className="flex justify-between items-center">
                    <span className={`text-xs ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>Total Users</span>
                    <span className={`text-lg font-bold ${isDark ? 'text-white' : 'text-gray-900'}`}>{stats.total_users}</span>
                  </div>
                </CardContent>
              </Card>
              <Card className={`${isDark ? 'bg-gray-800/50 border-gray-700' : 'bg-white border-gray-200'}`}>
                <CardContent className="p-3">
                  <div className="flex justify-between items-center">
                    <span className={`text-xs ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>Active Users</span>
                    <span className="text-lg font-bold text-green-400">{stats.active_users}</span>
                  </div>
                </CardContent>
              </Card>
              <Card className={`${isDark ? 'bg-gray-800/50 border-gray-700' : 'bg-white border-gray-200'}`}>
                <CardContent className="p-3">
                  <div className="flex justify-between items-center">
                    <span className={`text-xs ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>Content</span>
                    <span className="text-lg font-bold text-purple-400">{stats.total_content}</span>
                  </div>
                </CardContent>
              </Card>
            </div>
          )}
        </div>

        {/* Main Content */}
        <div className="flex-1 p-4 md:p-6 lg:p-8 w-full lg:w-auto overflow-x-hidden">
          {activeTab === 'analytics' && <AnalyticsDashboard stats={stats} />}
          {activeTab === 'users' && <UserManagement />}
          {activeTab === 'contract' && <ContractTierManagement />}
          {activeTab === 'content' && <ContentManagement />}
          {activeTab === 'bulk-videos' && <BulkVideoManagement />}
          {activeTab === 'bank' && <BankWithdrawalManagement />}
          {activeTab === 'audit' && <EnhancedAuditLogs />}
          {activeTab === 'faq' && <FAQManagement />}
          {activeTab === 'messages' && <MessagingPanel />}
          {activeTab === 'settings' && <AdminSettings />}
        </div>
      </div>
    </div>
  );
};

export default AdminDashboard;
